<template>
  <form class="quote-form" @submit.prevent="onSubmit">
    <h3 class="form-title">Tus datos</h3>

    <label class="block">
      <span>Nombre completo</span>
      <input v-model.trim="form.contactName" type="text" required />
    </label>

    <label class="block">
      <span>Correo electrónico</span>
      <input v-model.trim="form.contactEmail" type="email" required />
    </label>

    <label class="block">
      <span>WhatsApp (opcional)</span>
      <input v-model.trim="form.contactPhone" type="tel" placeholder="3001234567" />
    </label>

    <p v-if="autoFilledNotice" class="hint">
      Tomamos las medidas directamente de tu modelo. Si necesitas escalarlo,
      ajústalas en las opciones avanzadas.
    </p>

    <!-- Todo lo técnico queda plegado: el visitante solo necesita archivo +
         nombre + un dato de contacto para pedir su cotización (R10). -->
    <details class="advanced">
      <summary>Opciones avanzadas — material, cantidad, medidas y observaciones</summary>

      <div class="advanced-body">
        <fieldset>
          <legend>Dimensiones (mm)</legend>
          <div class="dim-grid">
            <label>
              <span>Largo (X)</span>
              <input
                v-model.number="form.dimX"
                type="number"
                min="0"
                step="0.1"
                required
                @input="markEdited('dimX')"
              />
            </label>
            <label>
              <span>Ancho (Y)</span>
              <input
                v-model.number="form.dimY"
                type="number"
                min="0"
                step="0.1"
                required
                @input="markEdited('dimY')"
              />
            </label>
            <label>
              <span>Alto (Z)</span>
              <input
                v-model.number="form.dimZ"
                type="number"
                min="0"
                step="0.1"
                required
                @input="markEdited('dimZ')"
              />
            </label>
          </div>
        </fieldset>

        <label class="block">
          <span>Material</span>
          <select v-model="form.materialId" required>
            <option disabled :value="null">Selecciona un material</option>
            <option v-for="m in materials" :key="m.id" :value="m.id">
              {{ m.displayName }}
            </option>
          </select>
        </label>

        <label v-if="categories.length" class="block">
          <span>Tipo de impresión</span>
          <select v-model="form.categoryId" @change="onCategoryChange">
            <option disabled :value="null">Selecciona un tipo</option>
            <option v-for="c in categories" :key="c.id" :value="c.id">
              {{ c.label }}
            </option>
          </select>
          <small v-if="autoSelectedCategory" class="hint">
            Sugerido automáticamente según el volumen de la pieza
            ({{ formatVolume(effectiveVolumeCm3) }} cm³). Puedes cambiarlo.
          </small>
        </label>

        <label class="block">
          <span>Cantidad</span>
          <input v-model.number="form.quantity" type="number" min="1" step="1" required />
        </label>

        <label class="block">
          <span>Observaciones (opcional)</span>
          <textarea
            v-model.trim="form.observations"
            rows="3"
            maxlength="500"
            placeholder="Acabado deseado, color preferido, fecha límite, tolerancias críticas…"
          />
          <small class="char-count">{{ (form.observations || '').length }} / 500</small>
        </label>
      </div>
    </details>

    <p v-if="needsDimensions" class="hint hint-warn" role="status">
      Todavía no tenemos las medidas de la pieza. Ábrelas en las opciones
      avanzadas y complétalas para continuar.
    </p>

    <button type="submit" class="button btn--block submit-btn" :disabled="!isValid || submitting">
      {{ submitting ? 'Procesando…' : 'Continuar con la cotización' }}
    </button>

    <p class="legal-note">
      Al enviar tu solicitud aceptas nuestra
      <router-link to="/privacidad">política de privacidad</router-link>.
    </p>

    <p v-if="errorMessage" class="err-msg" role="alert">{{ errorMessage }}</p>
  </form>
</template>

<script>
import { getMaterials, getPrintCategories } from '@/services/print3dService';

const initialForm = () => ({
  dimX: null,
  dimY: null,
  dimZ: null,
  materialId: null,
  categoryId: null,
  quantity: 1,
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  observations: '',
});

export default {
  name: 'QuoteForm',
  props: {
    hasFile: { type: Boolean, default: false },
    file: { type: File, default: null },
    autoFillDimensions: { type: Object, default: null },
    // Volumen real de la malla (mm³) calculado por el visor; alimenta la
    // autoselección del "Tipo de impresión".
    volumeMm3: { type: Number, default: null },
    submitting: { type: Boolean, default: false },
  },
  emits: ['request-preview'],
  data() {
    return {
      materials: [],
      categories: [],
      form: initialForm(),
      editedFields: {},
      errorMessage: '',
      autoFilledNotice: false,
      autoSelectedCategory: false,
    };
  },
  computed: {
    isValid() {
      const f = this.form;
      return (
        this.hasFile &&
        f.dimX > 0 &&
        f.dimY > 0 &&
        f.dimZ > 0 &&
        f.materialId !== null &&
        f.quantity >= 1 &&
        f.contactName.length > 0 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.contactEmail)
      );
    },
    // Si el visor no pudo calcular las medidas, hay que avisarlo: el botón
    // queda deshabilitado y el campo vive plegado en opciones avanzadas.
    needsDimensions() {
      const f = this.form;
      return this.hasFile && !(f.dimX > 0 && f.dimY > 0 && f.dimZ > 0);
    },
    // Volumen en cm³ para autoseleccionar la categoría: prioriza el volumen real
    // de la malla; si no hay, cae a la caja envolvente (dimX·dimY·dimZ).
    effectiveVolumeCm3() {
      if (this.volumeMm3 != null && this.volumeMm3 > 0) {
        return this.volumeMm3 / 1000;
      }
      const { dimX, dimY, dimZ } = this.form;
      if (dimX > 0 && dimY > 0 && dimZ > 0) {
        return (dimX * dimY * dimZ) / 1000;
      }
      return null;
    },
  },
  watch: {
    autoFillDimensions: {
      handler(newDims) {
        if (!newDims) return;
        // Sólo rellenar campos que el usuario no haya tocado
        if (!this.editedFields.dimX) this.form.dimX = newDims.x;
        if (!this.editedFields.dimY) this.form.dimY = newDims.y;
        if (!this.editedFields.dimZ) this.form.dimZ = newDims.z;
        this.autoFilledNotice = true;
      },
      deep: true,
    },
    effectiveVolumeCm3() {
      this.autoSelectCategory();
    },
  },
  async mounted() {
    const [{ materials }, { categories }] = await Promise.all([
      getMaterials(),
      getPrintCategories(),
    ]);
    this.materials = materials;
    // Valor por defecto para que el material no bloquee el envío cuando el
    // visitante no abre las opciones avanzadas.
    if (this.form.materialId === null && materials.length > 0) {
      this.form.materialId = materials[0].id;
    }
    this.categories = categories;
    this.autoSelectCategory();
  },
  methods: {
    markEdited(field) {
      this.editedFields[field] = true;
    },
    onCategoryChange() {
      this.editedFields.categoryId = true;
      this.autoSelectedCategory = false;
    },
    // Preselecciona la categoría cuyo rango [min, max) contiene el volumen.
    // Categorías sin umbrales (ambos null) son sólo de selección manual.
    // No sobreescribe si el usuario ya eligió manualmente.
    autoSelectCategory() {
      if (this.editedFields.categoryId) return;
      const vol = this.effectiveVolumeCm3;
      if (vol == null || !this.categories.length) return;
      const match = this.categories.find((c) => {
        const hasBounds = c.minVolumeCm3 != null || c.maxVolumeCm3 != null;
        if (!hasBounds) return false;
        const aboveMin = c.minVolumeCm3 == null || vol >= c.minVolumeCm3;
        const belowMax = c.maxVolumeCm3 == null || vol < c.maxVolumeCm3;
        return aboveMin && belowMax;
      });
      if (match) {
        this.form.categoryId = match.id;
        this.autoSelectedCategory = true;
      }
    },
    formatVolume(v) {
      return v != null ? Number(v).toFixed(2) : '—';
    },
    onSubmit() {
      if (!this.isValid) return;
      this.errorMessage = '';
      this.$emit('request-preview', {
        dimensionsMm: {
          x: this.form.dimX,
          y: this.form.dimY,
          z: this.form.dimZ,
        },
        materialId: this.form.materialId,
        categoryId: this.form.categoryId,
        quantity: this.form.quantity,
        contactName: this.form.contactName,
        contactEmail: this.form.contactEmail,
        contactPhone: this.form.contactPhone,
        observations: this.form.observations,
      });
    },
    reset() {
      this.form = initialForm();
      if (this.materials.length > 0) this.form.materialId = this.materials[0].id;
      this.editedFields = {};
      this.autoFilledNotice = false;
      this.autoSelectedCategory = false;
    },
    setError(message) {
      this.errorMessage = message;
    },
  },
};
</script>

<style scoped>
.quote-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: white;
  border: 1px solid #9DB2BF;
  border-radius: 12px;
  padding: 1.2rem;
}

.form-title {
  font-family: "Inter", sans-serif;
  font-size: 1.05rem;
  color: #111827;
  margin: 0.4rem 0 0;
}

form input, form select {
  margin-bottom: 0 !important;
}

fieldset {
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  padding: 0.8rem;
  margin: 0;
}

fieldset legend {
  padding: 0 0.4rem;
  color: #4F5665;
  font-size: 0.9rem;
}

.dim-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.6rem;
}

.dim-grid label,
.block {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
  color: #4F5665;
}

.block {
  width: 100%;
}

input[type="number"],
input[type="text"],
input[type="email"],
input[type="tel"],
select,
textarea {
  border: 1px solid #0B132A;
  border-radius: 6px;
  padding: 0.55rem 0.7rem;
  font-size: 16px;
  min-height: 44px;
  font-family: inherit;
  color: #2C3E50;
  background: white;
}

textarea {
  resize: vertical;
  min-height: 70px;
  max-height: 200px;
  line-height: 1.4;
}

input:focus,
select:focus,
textarea:focus {
  outline: 2px solid #526D82;
  outline-offset: 1px;
}

.char-count {
  color: #64748b;
  font-size: 0.75rem;
  text-align: right;
  margin-top: 0.15rem;
}

.submit-btn {
  background: #526D82;
  color: white;
  border: none;
  padding: 0.7rem 1rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
}

.submit-btn:hover:not(:disabled) {
  background: #3D566B;
}

.submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.hint {
  font-size: 0.8rem;
  color: #4F5665;
  margin: 0.4rem 0 0;
  font-style: italic;
}

.hint-warn {
  font-style: normal;
  background: rgba(234, 179, 8, 0.12);
  color: #854D0E;
  border-radius: 6px;
  padding: 0.5rem 0.7rem;
}

/* Bloque plegable con lo técnico (dimensiones, material, cantidad, notas). */
.advanced {
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  background: #F8FAFC;
}

.advanced > summary {
  cursor: pointer;
  padding: 0.7rem 0.9rem;
  font-family: "Jost", sans-serif;
  font-size: 0.92rem;
  color: #4F5665;
  list-style-position: inside;
}

.advanced > summary:focus-visible {
  outline: 2px solid #526D82;
  outline-offset: 2px;
  border-radius: 6px;
}

.advanced[open] > summary {
  border-bottom: 1px solid #E2E8F0;
  color: #2C3E50;
}

.advanced-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.9rem;
}

.ok-msg {
  background: rgba(22, 163, 74, 0.1);
  color: #166534;
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  margin: 0;
  font-size: 0.9rem;
}

.err-msg {
  background: rgba(185, 28, 28, 0.08);
  color: #991B1B;
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  margin: 0;
  font-size: 0.9rem;
}

@media (max-width: 800px) {
  .dim-grid {
    grid-template-columns: 1fr;
  }
}

.legal-note {
  font-size: 0.8rem;
  color: #4F5665;
  margin: 0;
  text-align: center;
}

.legal-note a {
  color: #526D82;
}
</style>
